import createStyles from "draft-js-custom-styles";
import {convertFromHTML} from "draft-convert";
import {stateFromElement, CustomBlockFn, CustomInlineFn} from "draft-js-import-element";
import {stateToHTML, Options as ExportOptions, RenderConfig, BlockStyleFn, EntityStyleFn} from "draft-js-export-html";
import {EditorState, ContentBlock, DraftInlineStyle, DraftStyleMap, EntityInstance} from "draft-js";
import {FontFamily, FontProviders, FontFormats} from "shared/xp/models/widgets";
import {DraftStyles, inlineStyleTypes, FONT_METADATA} from "shared/ui/experience/widgets/textEnhancedWidget/models";
import {DraftJSUtils} from "shared/ui/experience/widgets/textEnhancedWidget/utility";
import {textEditorDefaults} from "shared/ui/experience/widgets/textEnhancedWidget//TextEditorControls";
// This is the default prefix but we're still specifying it.
const prefix = "CUSTOM_";
const customStyles = ["font-size", "letter-spacing", "color", "font-weight", "font-family", "font-variant-ligatures"];
const { styles, customStyleFn: pluginCustomStyles, exporter } = createStyles(customStyles, prefix);

const customBlockStyleDataKeys = {
    textAlign: "text-align",
    lineHeight: "line-height",
};

const customStyleFn = (style: DraftInlineStyle, block: ContentBlock): DraftStyleMap => {
    const pluginStyles = pluginCustomStyles(style, block);

    // If there is a custom style for color set, we need to remove it so our mapped HIGHLIGHT style works.
    if(style.has(DraftStyles.HIGHLIGHT)) {
        delete pluginStyles.color;
    }

    return pluginStyles;
};

const blockElements = ["div", "h1", "h2", "h3", "h4", "h5", "h6"];

// It doesn't seem like draft.js likes <br /> tags.
// I might be able to manage them in textToEntity if we need to use them.
const convertFromClassic = convertFromHTML({
    htmlToStyle: (_nodeName: string, node: HTMLElement, currentStyle) => {
        const {innerText = "", innerHTML = "", style = {} as CSSStyleDeclaration} = node;
        // There is so very weird text generated by the existing text editor. If you apply a text shadow, press enter,
        //  toggle another style, then turn off the text shadow the new text will be wrapped with the inline style that
        // applies text shadow then clears it.
        if(innerText.length === 0 || innerHTML.length === 0) {
            // The HTML created by the old text widget is so poorly formed that there are empty elements which can
            // influence the style of our block.
            return currentStyle;
        } else if(style.fontStyle) { // I don't believe this is needed since we specify it in the custom map.
            return currentStyle.add(DraftStyles.ITALIC);
        } else if(style.textDecoration) {
            if(style.textDecoration === "line-through") {
                return currentStyle.add(DraftStyles.STRIKETHROUGH);
            } else if(style.textDecoration === "underline") {
                // This might not work.
                return currentStyle.add(DraftStyles.UNDERLINE);
            }
        } else if(style.textShadow) {
            // There is some weird style overrides going on with the old editor. It looks like most scenarios are no
            // contents or a child element clearing the style.
            if(style.textShadow !== "none") {
                return currentStyle.add(DraftStyles.SHADOW);
            } else {
                return currentStyle.remove(DraftStyles.SHADOW);
            }
        } else if(style.textTransform) {
            return currentStyle.add(DraftStyles.CAPITAL);
        } else if(style.fontVariant) {
            return currentStyle.add(DraftStyles.SMALLCAPS);
        } else if(style.color) {
            // It appears that we don't need to add the color VIA the styles object from createStyles.
            return currentStyle.add(`CUSTOM_COLOR_${style.color}`);
        } else if(style.letterSpacing) {
            return currentStyle.add(`CUSTOM_LETTER_SPACING_${style.letterSpacing}`);
        } else if(style.fontSize) {
            return currentStyle.add(`CUSTOM_FONT_SIZE_${style.fontSize}`);
        } else if(style.fontWeight) {
            return currentStyle.add(`CUSTOM_FONT_WEIGHT_${style.fontWeight}`);
        } else if(style.fontFamily) {
            return currentStyle.add(`CUSTOM_FONT_FAMILY_${style.fontFamily}`);
        // @ts-ignore unfortunatelly fontVariantLigatures available in TS since 3.6.2 version
        } else if(style.fontVariantLigatures) {
            // @ts-ignore unfortunatelly fontVariantLigatures available in TS since 3.6.2 version
            return currentStyle.add(`CUSTOM_FONT_VARIANT_LIGATURES_${style.fontVariantLigatures}`);
        }

        return currentStyle;
    },
    htmlToBlock: (nodeName: string, node: HTMLElement) => {
        // Might want to make this a little smarter in detecting a block level element.
        if (blockElements.some(elem => elem === nodeName)) {
            let blockData = {};
            const {
                textAlign = textEditorDefaults.alignment,
                lineHeight = textEditorDefaults.lineHeight.toString(),
            } = node.style;

            blockData[customBlockStyleDataKeys.textAlign] =  textAlign;
            blockData[customBlockStyleDataKeys.lineHeight] =  lineHeight;

            return {data: blockData};
        }
    }
});

const importFromHtmlCustomInlineFn: CustomInlineFn = (node: HTMLElement, {Style, Entity}) => {
    // There is so very weird text generated by the existing text editor. If you apply a text shadow, press enter,
    //  toggle another style, then turn off the text shadow the new text will be wrapped with the inline style that
    // applies text shadow then clears it.
    if(node.innerText.length !== 0 || node.innerHTML.length !== 0) {
        // TODO: Fix issue with multiple styles applied to a node.
        if(node.style.fontStyle) { // This does not work with the custom style map.
            return Style(DraftStyles.ITALIC);
        } else if(node.style.textDecoration) {
            if(node.style.textDecoration === "line-through") {
                return Style(DraftStyles.STRIKETHROUGH);
            } else if(node.style.textDecoration === "underline") {
                return Style(DraftStyles.UNDERLINE);
            }
        } else if(node.style.textShadow) {
                return Style(DraftStyles.SHADOW);
        } else if(node.style.textTransform) {
            return Style(DraftStyles.CAPITAL);
        } else if(node.style.fontVariant) {
            return Style(DraftStyles.SMALLCAPS);
        } else if(node.style.color) {
            // It appears that we don't need to add the color VIA the styles object from createStyles.
            return Style(`CUSTOM_COLOR_${node.style.color}`);
        } else if(node.style.letterSpacing) {
            return Style(`CUSTOM_LETTER_SPACING_${node.style.letterSpacing}`);
        } else if(node.style.fontSize) {
            return Style(`CUSTOM_FONT_SIZE_${node.style.fontSize}`);
        } else if(node.style.fontWeight) {
            return Style(`CUSTOM_FONT_WEIGHT_${node.style.fontWeight}`);
        } else if(node.style.fontFamily) {
            return Style(`CUSTOM_FONT_FAMILY_${node.style.fontFamily}`);
        // @ts-ignore unfortunatelly fontVariantLigatures available in TS since 3.6.2 version
        } else if(node.style.fontVariantLigatures) {
            // @ts-ignore unfortunatelly fontVariantLigatures available in TS since 3.6.2 version
            return Style(`CUSTOM_FONT_VARIANT_LIGATURES_${node.style.fontVariantLigatures}`);
        }

        if (["a", "span"].indexOf(node.tagName.toLowerCase()) !== -1 && node.getAttribute("data-font-provider")) {
            const fontProvider = node.getAttribute("data-font-provider");
            const fontFamily = node.getAttribute("data-font-family");
            const fontWeight = node.getAttribute("data-font-weight");
            const fontStyle = node.getAttribute("data-font-style");
            const cssFamilyNames = node.getAttribute("data-css-family-names");
            const fontFamilyProvider = {
                fontWeight,
                fontStyle,
                fontFamily,
                cssFamilyNames
            }
            let fontMetadata;

            switch (fontProvider) {
                case FontProviders.CUSTOM:
                    const fontFormat: string = node.getAttribute("data-format"); 
                    let format: FontFormats;
                    switch (fontFormat) {
                        case FontFormats.OpenType:
                            format = FontFormats.OpenType;
                            break;
                        case FontFormats.TrueType:
                            format = FontFormats.TrueType;
                            break;
                        case FontFormats.Woff:
                            format = FontFormats.Woff;
                            break;
                    }
                    fontMetadata =  {
                        customFontId: node.getAttribute("data-custom-font-id"),
                        format, 
                        path: node.getAttribute("data-path")
                    }
                    Object.assign(fontFamilyProvider, {
                        fontMetadata,
                        fontProvider: FontProviders.CUSTOM
                    })
                    break;
                case FontProviders.TYPEKIT:
                    fontMetadata = {
                        kitID: node.getAttribute("data-kit-id")
                    }
    
                    Object.assign(fontFamilyProvider, {
                        fontMetadata,
                        fontProvider: FontProviders.TYPEKIT
                    })
                    break;
                case FontProviders.FONTSDOTCOM:
                    fontMetadata = {
                        projectId: node.getAttribute("data-project-id")
                    }
                    Object.assign(fontFamilyProvider, {
                        fontMetadata,
                        fontProvider: FontProviders.FONTSDOTCOM
                    })
                    break;
                case FontProviders.GOOGLE:
                    Object.assign(fontFamilyProvider, {
                        fontProvider: FontProviders.GOOGLE
                    })
                    break;
                case FontProviders.STANDARD:
                    Object.assign(fontFamilyProvider, {
                        fontProvider: FontProviders.STANDARD
                    })
                    break;
            }

            if (node.tagName.toLowerCase() === "a") {
                Object.assign(fontFamilyProvider, {
                    url: node.getAttribute('href'),
                    target: node.getAttribute('target')
                })
            }

            return Entity(FONT_METADATA, DraftJSUtils.getFontMetadataEntityFromFontFamily(fontFamilyProvider as FontFamily));
        } else if (node.tagName.toLowerCase() === "a") {
            return Entity(FONT_METADATA, {
                url: node.getAttribute('href'),
                target: node.getAttribute('target')
            });
        }
    }
};

const importFromHtmlCustomBlockFn: CustomBlockFn = (node: HTMLElement) => {
    // Might want to make this a little smarter in detecting a block level element.
    if (blockElements.some(elem => elem === node.tagName.toLowerCase())) {
        let blockData = {};
        if (node.style.textAlign) {
            blockData[customBlockStyleDataKeys.textAlign] =  node.style.textAlign;
        }
        if (node.style.lineHeight) {
            blockData[customBlockStyleDataKeys.lineHeight] =  node.style.lineHeight;
        }

        return {data: blockData};
    }
};

const blockStyleFn = (block: ContentBlock): string => {
    const blockData = block.getData();
    const blockAlignment = blockData && blockData.get(customBlockStyleDataKeys.textAlign);
    const blockLineHeight = blockData && blockData.get(customBlockStyleDataKeys.lineHeight) || "";

    const isEmptyClassName = !block.getText() ? "etw-is-empty" : "";
    const alignmentClassName = blockAlignment ? `zmgs-${blockAlignment}-aligned-block` : "";
    const lineHeightClassName = blockLineHeight ?
        `zmgs-height-block-${blockLineHeight.replace(".", "-")}` : "";
    return `${alignmentClassName} ${lineHeightClassName} ${isEmptyClassName}`.trim();
};

// This isn't the same as the custom style map.
// Override the default tag and style.
const customInlineElementStyleMap = {};
// Override just the default style.
const customInlineStyleMap = {};

inlineStyleTypes.forEach(
    (inlineStyleTypeItem) => {
        const key = inlineStyleTypeItem.draftStyle;
        const {customStyles: innerCustomStyles} = inlineStyleTypeItem;
        if (inlineStyleTypeItem.exported) {
            customInlineElementStyleMap[key] = innerCustomStyles;
        }

        customInlineStyleMap[key] = innerCustomStyles.style;
    }
);

const exportBlockStyleFn: BlockStyleFn = (block: ContentBlock) => {
    const blockData = block.getData();
    const blockAlignment = blockData && blockData.get(customBlockStyleDataKeys.textAlign);
    const blockLineHeight = blockData && blockData.get(customBlockStyleDataKeys.lineHeight);

    let results: RenderConfig = undefined;
    if(blockAlignment || blockLineHeight) {
        results = { style: {textAlign: blockAlignment, lineHeight: blockLineHeight} };
    }

    return results;
};

const exportEntityStyleFn: EntityStyleFn = (entity: EntityInstance): RenderConfig => {
    const entityType = entity.getType();
    if (entityType === FONT_METADATA) {
        const entityData = entity.getData();
        const attributes = {};

        if (entityData.fontProvider) {
            const data: FontFamily = DraftJSUtils.getFontFamilyFromFontMetadataEntity(entityData);
            Object.assign(attributes, {
                "data-font-provider": data.fontProvider,
                "data-font-family": data.fontFamily,
                "data-font-style": data.fontStyle,
                "data-css-family-names": data.cssFamilyNames,
                "data-font-weight": data.fontWeight
            });
    
            // Custom fonts
            if(data.fontProvider === FontProviders.CUSTOM) {
                const metadata = data.fontMetadata;
                const customAttributes = {
                    "data-custom-font-id": metadata.customFontId,
                    "data-format": metadata.format,
                    "data-path": metadata.path
                }
    
                Object.assign(attributes, customAttributes)
            }
    
            // TypeKit fonts
            if(data.fontProvider === FontProviders.TYPEKIT) {
                const metadata = data.fontMetadata;
                const typekitAttributes = {
                    "data-kit-id": metadata.kitID
                }
    
                Object.assign(attributes, typekitAttributes)
            }
    
            // Fonts.com fonts
            if(data.fontProvider === FontProviders.FONTSDOTCOM) {
                const metadata = data.fontMetadata;
                const fontDotComAttributes = {
                    "data-project-id": metadata.projectId,
                }
    
                Object.assign(attributes, fontDotComAttributes)
            }
        }

        if (entityData.url) {
            const hyperlinkAttributes = {
                href: entityData.url,
                target: entityData.target
            }

            Object.assign(attributes, hyperlinkAttributes)
        }

        const result: RenderConfig = {
            element: entityData.url ? "a" : "span",
            attributes,
            style: {}
        };
        return result;
    }
};

const CUSTOM_FONT_STRING = `${prefix}FONT_FAMILY_`;
const CUSTOM_FONT_WEIGHT_STRING = `${prefix}FONT_WEIGHT_`;

const htmlFromEditorState = (editorState: EditorState) => {
    const exporterInlineStyles = exporter(editorState, customInlineStyleMap);

    // draft-js-custom-styles exporter returns malformed objects when the style name contains underscores
    // this occurs in custom fonts and because the key retains the full original string we're able to repair it here
    let sanitizedInlineStyles = {};
    Object.keys(exporterInlineStyles).forEach(key => {
        const styleStringArr = key.split(CUSTOM_FONT_STRING);
        if (styleStringArr[1] && styleStringArr[1].includes("_")) {
            sanitizedInlineStyles[key] = {
                style: {fontFamily: styleStringArr[1]}
            };
        } else {
            sanitizedInlineStyles[key] = exporterInlineStyles[key];
        }
    });

    const inlineStyles = {
        ...sanitizedInlineStyles,
        // It appears that if there's any unexpected data that the conversion doesn't work
        ...customInlineElementStyleMap,
    };

    const options: ExportOptions = {
        defaultBlockTag: "div",
        inlineStyles,
        blockStyleFn: exportBlockStyleFn,
        entityStyleFn: exportEntityStyleFn
    };

    const currentContent = editorState.getCurrentContent();
    // It might be possible to strip newline characters as part of the export itself instead of a regex.
    return stateToHTML(currentContent, options).replace(/\n/gi, "");
};

const editorStateFromHtml = (text: string) => {
    const contentStateFromHTML = convertFromClassic(text);
    const element = document.createElement("span");
    element.innerHTML = text;

    const contentState = stateFromElement(element, {
        customInlineFn: importFromHtmlCustomInlineFn,
        customBlockFn: importFromHtmlCustomBlockFn
    });

    return {contentStateFromHTML, contentState};
};

export {
    styles,
    customStyleFn,
    exporter,
    convertFromClassic,
    convertFromHTML,
    importFromHtmlCustomInlineFn,
    customInlineStyleMap,
    customInlineElementStyleMap,
    htmlFromEditorState,
    editorStateFromHtml,
    blockStyleFn,
    customBlockStyleDataKeys,
    CUSTOM_FONT_STRING,
    CUSTOM_FONT_WEIGHT_STRING
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textEnhancedWidget/editorStateConverters.ts